<template>
  <div class="appointmentRecord">
    <ds-header title="预约记录管理"></ds-header>

    <!-- 搜索筛选表单 -->
    <a-form layout="inline" style="margin-bottom: 10px">
      <a-form-item label="预约日期：">
        <a-range-picker
          v-model="searchForm.dateRange"
          value-format="YYYY-MM-DD"
          format="YYYY-MM-DD"
          :placeholder="['开始日期', '结束日期']"
          style="width: 380px"
          :getCalendarContainer="(triggerNode) => triggerNode.parentNode"
        />
      </a-form-item>
      <a-form-item label="预约类型：">
        <a-select
          v-model="searchForm.typeId"
          placeholder="请选择预约类型"
          style="width: 200px"
          allowClear
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option v-for="item in typeOptions" :key="item.id" :value="item.id">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="状态：">
        <a-select
          v-model="searchForm.status"
          placeholder="请选择状态"
          style="width: 200px"
          allowClear
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option v-for="item in statusOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="手机号：">
        <a-input v-model.trim="searchForm.phone" placeholder="请输入" style="width: 200px" />
      </a-form-item>
      <a-form-item label="企业名称：">
        <a-input v-model.trim="searchForm.companyName" placeholder="请输入" style="width: 200px" />
      </a-form-item>
      <a-form-item label="所属行业：">
        <a-input v-model.trim="searchForm.industry" placeholder="请输入" style="width: 200px" />
      </a-form-item>
      <a-form-item label="银行：">
        <a-select
          v-model="searchForm.bank"
          placeholder="请选择银行"
          style="width: 200px"
          allowClear
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option v-for="item in bankOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="发展阶段：">
        <a-select
          v-model="searchForm.developmentStage"
          placeholder="请选择发展阶段"
          style="width: 200px"
          allowClear
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option v-for="item in stageOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <span>
        <a-button type="primary" @click="search" icon="search">查询</a-button>
        <a-button style="margin-left: 8px" @click="resetSearchForm" icon="redo">重置</a-button>
      </span>
    </a-form>

    <!-- 导出操作区 -->
    <div style="margin: 10px 0">
      <a-button type="primary" :disabled="selectedRowKeys.length === 0" @click="exportData(true)">
        <a-icon type="download" />导出选中{{ selectedRowKeys.length ? `(${selectedRowKeys.length})` : "" }}
      </a-button>
      <a-button style="margin-left: 8px" @click="exportData(false)">
        <a-icon type="download" />按条件导出全部
      </a-button>
    </div>

    <!-- 数据表格 -->
    <a-spin :spinning="loading" tip="加载中...">
      <a-table
        :rowKey="(record) => record.id"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange, fixed: true }"
        :columns="table.columns"
        :data-source="table.tableData"
        :pagination="table.pagination.total ? {
          current: table.pagination.page,
          pageSize: table.pagination.size,
          total: table.pagination.total,
          pageSizeOptions: table.pagination.pageSizeOptions,
          showSizeChanger: table.pagination.showSizeChanger,
          showQuickJumper: table.pagination.showQuickJumper,
          showTotal: table.pagination.showTotal,
        } : false"
        @change="pageChange"
        bordered
        :scroll="{ x: 2290 }"
      >
        <!-- 预约时段列（多时段逗号分隔） -->
        <div slot="slotsText" slot-scope="text">
          <a-tooltip>
            <template slot="title">
              <span>{{ text }}</span>
            </template>
            <div class="ellipsis-line">{{ text }}</div>
          </a-tooltip>
        </div>

        <!-- 企业名称列 -->
        <div slot="companyName" slot-scope="text">
          <a-tooltip>
            <template slot="title">
              <span>{{ text || "-" }}</span>
            </template>
            <div class="ellipsis-line">{{ text || "-" }}</div>
          </a-tooltip>
        </div>

        <!-- 备注列 -->
        <div slot="remark" slot-scope="text">
          <a-tooltip>
            <template slot="title">
              <span>{{ text || "-" }}</span>
            </template>
            <div class="ellipsis-line">{{ text || "-" }}</div>
          </a-tooltip>
        </div>

        <!-- 状态列 -->
        <span slot="status" slot-scope="text">
          <a-tag v-if="getStatusConfig(text).style" :style="getStatusConfig(text).style">
            {{ getStatusConfig(text).text }}
          </a-tag>
          <a-tag v-else :color="getStatusConfig(text).color">
            {{ getStatusConfig(text).text }}
          </a-tag>
        </span>

        <!-- 操作列 -->
        <span slot="action" slot-scope="text, record" class="action">
          <a-button type="link" @click="showDetail(record)">查看</a-button>
          <a-button type="link" v-if="record.closable" @click="closeAppointment(record)">关闭</a-button>
          <a-button type="link" @click="exportData(true, [record.id])">导出</a-button>
        </span>
      </a-table>
    </a-spin>

    <!-- 预约详情弹窗 -->
    <a-modal
      v-model="detailVisible"
      title="预约详情"
      :footer="null"
      width="720px"
      wrapClassName="rh-appointment-detail-modal"
      @cancel="detailVisible = false"
    >
      <a-descriptions v-if="detailRecord" bordered size="small" :column="2">
        <a-descriptions-item label="预约编号">{{ detailRecord.appointmentNo || "-" }}</a-descriptions-item>
        <a-descriptions-item label="状态">
          {{ detailRecord.status === 1 ? "已预约" : "已取消" }}
        </a-descriptions-item>
        <a-descriptions-item label="预约类型">{{ detailRecord.typeName || "-" }}</a-descriptions-item>
        <a-descriptions-item label="预约日期">{{ detailRecord.appointmentDate || "-" }}</a-descriptions-item>
        <a-descriptions-item label="预约时段" :span="2">
          {{ detailRecord.slotsText || "-" }}
        </a-descriptions-item>
        <a-descriptions-item label="预约人姓名">{{ detailRecord.userName || "-" }}</a-descriptions-item>
        <a-descriptions-item label="手机号">{{ detailRecord.phone || "-" }}</a-descriptions-item>
        <a-descriptions-item label="企业名称">{{ detailRecord.companyName || "-" }}</a-descriptions-item>
        <a-descriptions-item label="所属行业">{{ detailRecord.industry || "-" }}</a-descriptions-item>
        <a-descriptions-item label="银行">{{ detailRecord.bankDesc || "-" }}</a-descriptions-item>
        <a-descriptions-item label="发展阶段">{{ detailRecord.developmentStageDesc || "-" }}</a-descriptions-item>
        <a-descriptions-item label="来访类型">{{ detailRecord.firstVisitDesc || "-" }}</a-descriptions-item>
        <a-descriptions-item label="人数">{{ detailRecord.peopleNum || "-" }}</a-descriptions-item>
        <a-descriptions-item label="预约时间">{{ detailRecord.createTime || "-" }}</a-descriptions-item>
        <a-descriptions-item label="取消时间">{{ detailRecord.cancelTime || "-" }}</a-descriptions-item>
        <a-descriptions-item label="备注" :span="2">{{ detailRecord.remark || "-" }}</a-descriptions-item>
      </a-descriptions>
    </a-modal>

    <!-- 关闭预约单确认弹窗 -->
    <a-modal
      title="关闭预约单"
      :visible="closeVisible"
      :confirm-loading="confirmLoading"
      okText="确认关闭"
      cancelText="取消"
      @ok="handleCloseOk"
      @cancel="closeVisible = false"
    >
      <p>确定要关闭预约编号「{{ currentRow.appointmentNo }}」的预约单吗？</p>
      <p style="color: #ff4d4f;">关闭后状态将变为「已取消」，时段名额即时释放可被他人预约，该操作不可恢复！</p>
    </a-modal>
  </div>
</template>

<script>
import * as api from '@/api/rongHubManagement';
import { dealExcelRespose } from '@/utils/download';
import { StatusOptions, BankOptions, StageOptions, StatusConfig, TableColumns } from './help';

export default {
  name: 'RongHubAppointmentRecord',
  data() {
    return {
      // 下拉选项
      statusOptions: StatusOptions,
      bankOptions: BankOptions,
      stageOptions: StageOptions,
      // 预约类型下拉项（两级树铺平：招商 / 场地租赁）
      typeOptions: [],
      // 加载状态
      loading: false,
      // 搜索表单
      searchForm: {
        dateRange: [],
        typeId: undefined,
        status: undefined,
        phone: '',
        companyName: '',
        industry: '',
        bank: undefined,
        developmentStage: undefined
      },
      // 表格配置
      table: {
        columns: TableColumns,
        tableData: [],
        pagination: {
          pageSizeOptions: ['10', '20', '30', '50', '100'],
          showSizeChanger: true,
          page: 1,
          size: 10,
          total: 0,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`
        }
      },
      // 勾选的预约单ID
      selectedRowKeys: [],
      // 详情弹窗
      detailVisible: false,
      detailRecord: null,
      // 关闭确认弹窗
      closeVisible: false,
      confirmLoading: false,
      // 当前操作行
      currentRow: {}
    };
  },
  mounted() {
    this.getTypeOptions();
    this.getList();
  },
  methods: {
    // ========== 数据获取 ==========

    /**
     * 拉取预约类型树并铺平为下拉选项（父类名 / 子类名）
     */
    async getTypeOptions() {
      try {
        const res = await api.getAppointmentTypes();
        if (res.code === this.$SUCCESS_CODE && Array.isArray(res.data)) {
          const options = [];
          res.data.forEach((parent) => {
            (parent.children || []).forEach((child) => {
              options.push({ id: child.id, label: `${parent.typeName} / ${child.typeName}` });
            });
          });
          this.typeOptions = options;
        }
      } catch (error) {
        console.log('获取预约类型失败:', error);
      }
    },

    /**
     * 获取预约列表
     */
    async getList() {
      this.loading = true;
      try {
        const res = await api.getAppointmentList({
          page: this.table.pagination.page,
          size: this.table.pagination.size,
          ...this.buildSearchParams()
        });
        if (res.code === this.$SUCCESS_CODE && res.data) {
          // 时段明细取最早开始-最晚结束拼接为区间文本，供表格与详情共用
          this.table.tableData = (res.data.items || []).map((item) => ({
            ...item,
            slotsText: this.buildSlotRange(item.slots)
          }));
          this.table.pagination.total = res.data.total || 0;
        } else {
          this.table.tableData = [];
          this.table.pagination.total = 0;
        }
      } catch (error) {
        console.error('获取预约列表失败:', error);
        this.$message.error('获取列表失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },

    // ========== 搜索操作 ==========

    /**
     * 时段明细取最早开始-最晚结束拼接为区间（如 13:00-16:00）
     * @param {Array} slots [{ slotId, startTime, endTime }]
     */
    buildSlotRange(slots) {
      if (!Array.isArray(slots) || slots.length === 0) {
        return '';
      }
      // 按开始时间排序后取首尾，避免依赖接口返回顺序
      const sorted = [...slots].sort((a, b) => a.startTime.localeCompare(b.startTime));
      return `${sorted[0].startTime}-${sorted[sorted.length - 1].endTime}`;
    },

    /**
     * 组装查询参数（过滤空值，日期范围拆为 startDate/endDate）
     */
    buildSearchParams() {
      const { dateRange, typeId, status, phone, companyName, industry, bank, developmentStage } = this.searchForm;
      const params = {};
      if (Array.isArray(dateRange) && dateRange.length === 2) {
        params.startDate = dateRange[0];
        params.endDate = dateRange[1];
      }
      if (typeId !== undefined && typeId !== null) params.typeId = typeId;
      if (status !== undefined && status !== null) params.status = status;
      if (bank !== undefined && bank !== null) params.bank = bank;
      if (developmentStage !== undefined && developmentStage !== null) params.developmentStage = developmentStage;
      if (phone) params.phone = phone;
      if (companyName) params.companyName = companyName;
      if (industry) params.industry = industry;
      return params;
    },

    /**
     * 搜索
     */
    search() {
      this.table.pagination.page = 1;
      this.getList();
    },

    /**
     * 重置搜索表单
     */
    resetSearchForm() {
      this.searchForm = {
        dateRange: [],
        typeId: undefined,
        status: undefined,
        phone: '',
        companyName: '',
        industry: '',
        bank: undefined,
        developmentStage: undefined
      };
      this.table.pagination.page = 1;
      this.getList();
    },

    /**
     * 分页变化
     */
    pageChange(option) {
      this.table.pagination.page = option.current;
      this.table.pagination.size = option.pageSize;
      this.getList();
    },

    /**
     * 勾选变更
     */
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
    },

    // ========== 状态配置 ==========

    /**
     * 获取状态显示配置
     */
    getStatusConfig(status) {
      return StatusConfig[status] || { text: '未知', color: 'default' };
    },

    // ========== 导出 ==========

    /**
     * 导出预约单Excel
     * @param {Boolean} selected true按指定ID导出，false按查询条件全量导出
     * @param {Array} rowIds 指定导出的预约单ID（操作列单行导出时传 [record.id]），不传则用表格勾选项
     */
    async exportData(selected, rowIds) {
      const loading = this.$message.loading('导出中...', 0);

      try {
        const params = this.buildSearchParams();
        if (selected) {
          params.ids = (rowIds || this.selectedRowKeys).join(',');
        }
        const res = await api.exportAppointment(params);

        // 处理Blob响应
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        dealExcelRespose(blob, '预约列表.xlsx');

        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出预约列表失败:', error);

        // 判断是否是业务错误（接口失败时返回 HTTP 400 及 JSON 错误信息）
        if (error.response && error.response.data) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              this.$message.error('导出失败：' + (errorData.msg || '未知错误'));
            } catch (e) {
              this.$message.error('导出失败，请稍后重试');
            }
          };
          reader.readAsText(error.response.data);
        } else {
          this.$message.error('导出失败：' + (error.message || '网络错误'));
        }
      } finally {
        loading();
      }
    },

    // ========== 详情 ==========

    /**
     * 查看详情
     */
    showDetail(record) {
      this.detailRecord = record;
      this.detailVisible = true;
    },

    // ========== 关闭预约单 ==========

    /**
     * 关闭预约单（弹出确认框）
     */
    closeAppointment(record) {
      this.currentRow = record;
      this.closeVisible = true;
    },

    /**
     * 确认关闭
     */
    async handleCloseOk() {
      this.confirmLoading = true;

      try {
        const res = await api.closeAppointment(this.currentRow.id);
        if (res.code === this.$SUCCESS_CODE) {
          this.$message.success('关闭成功');
          this.closeVisible = false;
          // 按「已预约」筛选时行会消失，处理末页回退
          if (this.searchForm.status === 1 && this.table.tableData.length === 1 && this.table.pagination.page > 1) {
            this.table.pagination.page -= 1;
          }
          this.getList();
        } else {
          this.$message.error(res.msg || '关闭失败');
        }
      } catch (error) {
        console.error('关闭预约单失败:', error);
        this.$message.error('关闭失败，请稍后重试');
      } finally {
        this.confirmLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.appointmentRecord {
  padding: 16px;

  .ellipsis-line {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // 所有单元格强制单行，超长省略，不折行
  ::v-deep .ant-table-tbody > tr > td {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  // 详情弹窗字段标题不换行（弹窗渲染在 body 下，scoped 样式作用不到，用全局样式块按弹窗类名限定）


  .action {
    .ant-btn-link {
      padding: 0;
      margin-right: 5px;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>

<style lang="scss">
// 预约详情弹窗渲染在 body 下，需全局样式按弹窗类名限定
.rh-appointment-detail-modal {
  // 标题列收缩到内容宽度且不换行（width:1% + nowrap 为表格布局经典收缩写法）
  .ant-descriptions-item-label {
    width: 1%;
    white-space: nowrap;
  }

  // 内容列承接剩余宽度，长文本正常换行
  .ant-descriptions-item-content {
    word-break: break-all;
  }
}
</style>
